import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell, Skeleton, } from '@cargomatic/ui';
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from 'utils/ui/classes';
import formatMoney from 'utils/formatters/Money';
import { sluggify } from 'utils/localization/utils';
import COLUMNS from './JobListColumns';
const DRAYAGE = 'drayage';
const ChargeRow = ({ charge, t }) => {
    var _a, _b;
    const message = charge.type === 'customer_adjustment'
        ? titleize((_a = charge.message) !== null && _a !== void 0 ? _a : '')
        : t(sluggify((_b = charge.message) !== null && _b !== void 0 ? _b : ''));
    return (React.createElement("tr", { className: "border-b border-border/50" },
        React.createElement("td", { className: "py-1.5 px-3 text-sm text-muted-foreground" }, message),
        React.createElement("td", { className: "py-1.5 px-3 text-sm text-right" },
            React.createElement("span", { className: cn(charge.value < 0 && 'text-destructive') }, formatMoney(charge.value, charge.currency)))));
};
const JobList = ({ context, ledgerId, ledgerType, jobs, lineItems, isLoading = true, isError, }) => {
    const { t } = useTranslation();
    const [expandedRows, setExpandedRows] = useState({});
    const lineItemLookup = useMemo(() => {
        if (!lineItems || lineItems.length === 0)
            return {};
        const ledgerProperty = context === 'invoice' ? 'receivables' : 'payables';
        const isShorthaul = ledgerType !== DRAYAGE;
        return lineItems.reduce((data, job) => {
            var _a;
            data[job._id.toString()] = ((_a = job[ledgerProperty]) !== null && _a !== void 0 ? _a : []).map((item) => {
                return Object.assign(Object.assign({}, item), { needs_equipment: !isShorthaul && !item.equipment });
            });
            return data;
        }, {});
    }, [lineItems, context, ledgerType]);
    const columns = COLUMNS[ledgerType] || [];
    const groupedJobs = useMemo(() => {
        if (!jobs)
            return [];
        const sorted = sortBy(jobs, ['driverName', 'jobDeliverBy']);
        const ledgerProperty = context === 'invoice' ? 'invoice_id' : 'payout_id';
        return sorted.map((job) => {
            const items = lineItemLookup[job.shipment_id] || [];
            const currentItems = items.filter((item) => item[ledgerProperty] === ledgerId);
            const total = currentItems.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0);
            return { job, charges: currentItems, total };
        });
    }, [jobs, lineItemLookup, context, ledgerId]);
    const toggleRow = (shipmentId) => {
        setExpandedRows((prev) => (Object.assign(Object.assign({}, prev), { [shipmentId]: !prev[shipmentId] })));
    };
    const localizeTitles = (column) => {
        let display_text = t(column.title, {
            count: column === null || column === void 0 ? void 0 : column.count,
        });
        if (column.titleize)
            display_text = titleize(display_text);
        return (React.createElement(TableHead, { "data-testid": "p-be4e0d7d", key: column.field, className: column.className }, display_text));
    };
    if (!jobs && !isLoading)
        return null;
    return (React.createElement("div", { "data-testid": "div-7df63e75", className: "overflow-x-auto" },
        React.createElement(Table, null,
            React.createElement(TableHeader, null,
                React.createElement(TableRow, null, columns.map((column) => localizeTitles(column)))),
            React.createElement(TableBody, null, isError ? (React.createElement(TableRow, null,
                React.createElement(TableCell, { colSpan: columns.length, className: "py-8" },
                    React.createElement("div", { className: "flex items-center justify-center gap-2 text-destructive" },
                        React.createElement(AlertCircle, { className: "h-4 w-4" }),
                        React.createElement("span", null, t('FAILED_TO_LOAD_LINE_ITEMS')))))) : isLoading && !isError ? (Array.from({ length: 5 }).map((_, i) => (React.createElement(TableRow, { key: `skel-${i}` }, Array.from({ length: columns.length || 6 }).map((_, j) => (React.createElement(TableCell, { key: `skel-${i}-${j}` },
                React.createElement(Skeleton, { className: "h-4 w-full" })))))))) : groupedJobs.length === 0 ? (React.createElement(TableRow, null,
                React.createElement(TableCell, { colSpan: columns.length, className: "text-center text-muted-foreground py-8" }, t('NO_DATA_FOUND')))) : (groupedJobs.map(({ job, charges, total }, groupIndex) => {
                const isExpanded = expandedRows[job.shipment_id];
                return (React.createElement(React.Fragment, { key: job.shipment_id || job._id },
                    React.createElement(TableRow, { "data-testid": `div-644b2292-${groupIndex}`, className: "cursor-pointer hover:bg-muted/50", onClick: () => toggleRow(job.shipment_id) }, columns.map((column, index) => {
                        const key = `${column.field}-${job._id}`;
                        if (index === 0) {
                            return (React.createElement(TableCell, { "data-testid": `div-4e0516cc-${index}`, key: key, className: column.className },
                                React.createElement("div", { className: "flex items-center gap-2" },
                                    isExpanded ? (React.createElement(ChevronDown, { className: "h-4 w-4 shrink-0" })) : (React.createElement(ChevronRight, { className: "h-4 w-4 shrink-0" })),
                                    column.value(job, total))));
                        }
                        return (React.createElement(TableCell, { "data-testid": `div-4e0516cc-${index}`, key: key, className: column.className }, column.value(job, total)));
                    })),
                    isExpanded && charges.length > 0 && (React.createElement(TableRow, null,
                        React.createElement(TableCell, { colSpan: columns.length, className: "p-0 bg-muted/30" },
                            React.createElement("div", { className: "py-2 px-8" },
                                React.createElement("table", { className: "w-auto min-w-[320px]" },
                                    React.createElement("thead", null,
                                        React.createElement("tr", { className: "border-b border-border" },
                                            React.createElement("th", { className: "py-1.5 px-3 text-left text-xs font-medium text-muted-foreground uppercase" }, t('CHARGE')),
                                            React.createElement("th", { className: "py-1.5 px-3 text-right text-xs font-medium text-muted-foreground uppercase" }, t('AMOUNT')))),
                                    React.createElement("tbody", null, charges.map((charge, idx) => (React.createElement(ChargeRow, { key: charge._id || idx, charge: charge, t: t })))))))))));
            }))))));
};
export default JobList;
JobList.propTypes = {
    context: PropTypes.string.isRequired,
    ledgerType: PropTypes.string.isRequired,
    ledgerId: PropTypes.string.isRequired,
    jobs: PropTypes.array.isRequired,
    lineItems: PropTypes.array,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
};
